import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { TrendingUp, TrendingDown, Remove } from '@mui/icons-material';
import { formatCurrency, getBalanceColor, formatPercentage } from '../utils/accountUtils';

interface BalanceDisplayProps {
  amount: number;
  currency: string;
  label?: string;
  size?: 'small' | 'medium' | 'large';
  showTrend?: boolean;
  trendValue?: number;
  previousAmount?: number;
}

const BalanceDisplay: React.FC<BalanceDisplayProps> = ({
  amount,
  currency,
  label,
  size = 'medium',
  showTrend = false,
  trendValue,
  previousAmount,
}) => {
  const getFontSize = () => {
    switch (size) {
      case 'small':
        return 'h6';
      case 'large':
        return 'h4';
      default:
        return 'h5';
    }
  };

  const getTrendIcon = () => {
    if (trendValue === undefined) return null;
    if (trendValue > 0) return <TrendingUp fontSize="small" />;
    if (trendValue < 0) return <TrendingDown fontSize="small" />;
    return <Remove fontSize="small" />;
  };

  const getTrendColor = () => {
    if (trendValue === undefined) return 'text.secondary';
    if (trendValue > 0) return 'success.main';
    if (trendValue < 0) return 'error.main';
    return 'text.secondary';
  };

  return (
    <Box>
      {label && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {label}
        </Typography>
      )}
      <Box display="flex" alignItems="center" gap={1}>
        <Typography
          variant={getFontSize()}
          sx={{
            color: getBalanceColor(amount),
            fontWeight: 'bold',
          }}
        >
          {formatCurrency(amount, currency)}
        </Typography>
        {showTrend && trendValue !== undefined && (
          <Tooltip
            title={
              previousAmount !== undefined
                ? `Previous: ${formatCurrency(previousAmount, currency)}`
                : ''
            }
          >
            <Box display="flex" alignItems="center" sx={{ color: getTrendColor() }}>
              {getTrendIcon()}
              <Typography variant="caption" sx={{ ml: 0.5 }}>
                {formatPercentage(trendValue)}
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default BalanceDisplay;
